import type { AuthUser, Capability, Role } from '../types';
import { ROUTES } from './routes';

/**
 * What to *render* for the signed-in user, from the server-computed
 * `capabilities` (`docs/api.md` §4.2).
 *
 * This is presentation only. It never replaces a server check — every admin
 * request is authorised by the API — and it deliberately does not map roles to
 * permissions: that table belongs to the backend (`role-capabilities.ts`), and a
 * copy here would drift from it.
 */

/** Capabilities that make the admin area useful. Holding any one opens it. */
export const ADMIN_AREA_CAPABILITIES: readonly Capability[] = [
  'projects:manage',
  'grants:manage',
  'users:manage',
  'admins:manage',
  'access-logs:read',
  'audit-events:read',
  'settings:manage',
];

export function hasCapability(user: AuthUser | null, capability: Capability): boolean {
  return user?.capabilities.includes(capability) ?? false;
}

export function canUseAdminArea(user: AuthUser | null): boolean {
  return ADMIN_AREA_CAPABILITIES.some((capability) => hasCapability(user, capability));
}

/**
 * Where a user lands after signing in when no protected route was requested.
 * SUPER_ADMIN and ADMIN receive admin capabilities from the server and land in the
 * admin area; BD_MEMBER and CLIENT land on the catalogue.
 */
export function homeRouteFor(user: AuthUser): string {
  return canUseAdminArea(user) ? ROUTES.admin.index : ROUTES.projects;
}

/** The Users section is shown to holders of `users:manage` (SUPER_ADMIN, ADMIN). */
export function canManageUsers(user: AuthUser | null): boolean {
  return hasCapability(user, 'users:manage');
}

/**
 * Roles the create and edit forms offer. Presentation of the server policy
 * (ADR-15, `apps/api/src/users/user-management.policy.ts`), which still decides:
 *
 * - administrator accounts need `admins:manage`, so an ADMIN is offered only
 *   BD_MEMBER and CLIENT;
 * - no API call may produce a SUPER_ADMIN (Manager item M-4), so it is never offered.
 *   The bootstrap CLI is the only way to create one.
 */
export function assignableRoles(actor: AuthUser | null): readonly Role[] {
  if (!canManageUsers(actor)) {
    return [];
  }

  return hasCapability(actor, 'admins:manage')
    ? ['ADMIN', 'BD_MEMBER', 'CLIENT']
    : ['BD_MEMBER', 'CLIENT'];
}

/**
 * Roles that can hold a project grant (ADR-16, `docs/api.md` §5.5). Administrators
 * open every project through `projects:open-any` and are never granted.
 *
 * Presentation only: the assignment screens show a note instead of checkboxes for
 * other roles, so they never offer a change certain to be refused. The API checks
 * every grant itself.
 */
export const GRANTABLE_ROLES: readonly Role[] = ['BD_MEMBER', 'CLIENT'];

export function canHoldGrants(role: Role): boolean {
  return GRANTABLE_ROLES.includes(role);
}

/**
 * Why the signed-in admin may not change `target`, or null when the screen should
 * offer the actions. The server re-checks every request and answers `403` on its
 * own; this only avoids offering a button that is certain to be refused.
 */
export function userActionBlock(
  actor: AuthUser | null,
  target: { readonly id: string; readonly role: Role },
): 'self' | 'authority' | null {
  if (actor === null || !canManageUsers(actor)) {
    return 'authority';
  }

  if (actor.id === target.id) {
    return 'self';
  }

  const isAdministrative = target.role === 'SUPER_ADMIN' || target.role === 'ADMIN';

  return isAdministrative && !hasCapability(actor, 'admins:manage') ? 'authority' : null;
}
